/**
 * Local login page (rendered OUTSIDE the app shell).
 *
 * Shown when an authenticated session is required (`setupState.enforced`)
 * and the user isn't logged in. Offers a local username/password form and,
 * when SSO is configured, a "Sign in with <provider>" button (the provider's
 * `displayName`; the identity provider is any OpenID Connect one).
 *
 * On success it navigates to `?returnTo=` when that is a page of this origin
 * (`safeReturnTo`), else to `/`. The SSO button carries the same checked
 * `returnTo` to `/api/auth/sso/login`, which checks it again.
 *
 * `?reason=` says why the user is here:
 * - `idle-timeout` — a "session timed out" banner (set by the auth gate when
 *   a live session expired);
 * - `no-access`, `locked`, `mfa-required` — the SSO callback refused the
 *   sign-in (no role in the provider's claim, a locked user, a second factor
 *   the policy asks for and the login did not show).
 * A locked local account is surfaced inline from the login response, not via
 * a query param.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api';
import { useAuth } from '../auth/AuthContext';
import { useCloud } from '../cloud';
import { safeReturnTo } from '../auth/safeReturnTo';
import { useActiveWorkspace } from '../runtime/ActiveWorkspaceContext';

/** The backend returns 401 `{error:'account locked'}` for a locked account
 *  with otherwise-valid credentials. `request()` preserves that parsed body
 *  on `ApiError.body`, so we can distinguish it from a plain bad-password
 *  401 and show a more actionable message. */
function isAccountLocked(err: ApiError): boolean {
  const body = err.body;
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { error?: unknown }).error === 'account locked'
  );
}

/** What the button calls the provider when the config names none: the
 *  backend's own default for `displayName` (`DISPLAY_NAME_DEFAULT` in its
 *  `sso-config-store.ts`), so a designer that leaves the field out reads the
 *  same as one that sends the default. */
const SSO_NAME_DEFAULT = 'single sign-on';

/** What the SSO callback's `?reason=` means to the person it turned away. */
const SSO_REFUSALS = new Map([
  ['no-access', 'Your account has no access to this designer. Ask its owner to add you.'],
  ['locked', 'This account is locked. Ask an administrator to unlock it.'],
  [
    'mfa-required',
    'This designer requires multi-factor sign-in. Sign in again with a second factor.',
  ],
]);

/** The SSO login URL, with the page to come back to when there is a real one.
 *  `safeReturnTo` answers `/` for a missing or unsafe value, and `/` is where
 *  a login lands anyway, so that is sent as no `returnTo` at all. */
function ssoLoginHref(rawReturnTo: string | null): string {
  const path = safeReturnTo(rawReturnTo);
  return path === '/'
    ? '/api/auth/sso/login'
    : `/api/auth/sso/login?returnTo=${encodeURIComponent(path)}`;
}

export function AuthLoginPage() {
  const { login, ssoConfig } = useAuth();
  // Pipebase Cloud signs people in through its identity provider only: the
  // backend refuses POST /api/auth/login there.
  const cloud = useCloud();
  const { refresh: refreshWorkspaces } = useActiveWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reason = params.get('reason');
  const refusal = reason === null ? undefined : SSO_REFUSALS.get(reason);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username, password);
      // The workspace list was read before anyone was signed in (a 401, so an
      // empty list) and is only re-read on request. Read it as this user, and
      // let the remembered workspace be checked against it, before the app
      // mounts and starts sending it.
      await refreshWorkspaces();
      // Never the raw query value: a crafted `returnTo` names another host
      // once the history API reads it (safeReturnTo.ts says how).
      navigate(safeReturnTo(params.get('returnTo')), { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 429
            ? 'Too many attempts — try again shortly.'
            : isAccountLocked(err)
              ? 'This account is locked. Contact an administrator.'
              : 'Invalid username or password.'
          : 'Login failed',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen ip-shell" data-testid="auth-login">
      <form className="auth-card" onSubmit={submit}>
        <h1>Sign in</h1>
        {reason === 'idle-timeout' && (
          <div className="badge badge--warn">
            Your session timed out. Sign in again.
          </div>
        )}
        {refusal && (
          <div className="badge badge--err" role="alert">
            {refusal}
          </div>
        )}
        {error && (
          <div className="badge badge--err" role="alert">
            {error}
          </div>
        )}
        {!cloud && (
          <>
            <label>
              Username
              <input
                data-testid="login-username"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
            </label>
            <label>
              Password
              <input
                data-testid="login-password"
                name="password"
                autoComplete="current-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <button data-testid="login-submit" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </>
        )}
        {ssoConfig?.configured && (
          <a
            className="auth-sso-link"
            data-testid="login-sso"
            href={ssoLoginHref(params.get('returnTo'))}
          >
            Sign in with {ssoConfig.displayName || SSO_NAME_DEFAULT}
          </a>
        )}
        {cloud && !ssoConfig?.configured && (
          <div className="badge badge--warn" role="status" data-testid="login-sso-missing">
            Sign-in is not set up for this designer yet. Try again in a minute.
          </div>
        )}
      </form>
    </div>
  );
}
